<template>
  <el-form
    ref="formRef"
    class="flex add-platform-users-form"
    require-asterisk-position="right"
    label-position="top"
    :model="formValues"
    :rules="rules"
  >
    <div class="up-form-box" :class="[{ 'one-line': oneColumn }]">
      <el-form-item prop="businessLicensePic" label="营业执照照片">
        <card-image-upload
          :action="UploadImageUrls.license"
          :readonly="disabled"
          :image="formValues.businessLicensePic"
          @on-success="(res: any) => {
            const { businessLicenseInfo, picResult } = res || {};

            formValues.name = businessLicenseInfo?.companyName;
            formValues.legalName = businessLicenseInfo?.artificialName;
            formValues.creditCode = businessLicenseInfo?.creditNo;
            formValues.address = businessLicenseInfo?.address;
            formValues.businessScope = businessLicenseInfo?.scope;
            formValues.businessLicensePic = picResult?.picUrl;

            if (businessLicenseInfo?.startTime && businessLicenseInfo?.operatingPeriod) {
              formValues.businessLicenseStartDate = changeTimeToFormat(businessLicenseInfo?.startTime);
              formValues.businessLicenseEndDate = changeTimeToFormat(businessLicenseInfo?.operatingPeriod);
              formValues.businessLicenseExpiryDate = [formValues.businessLicenseStartDate, formValues.businessLicenseEndDate];
              isLongTerm = longTimeValues.includes(businessLicenseInfo?.operatingPeriod);
            }
            formRef?.validate();
          }"
        />
      </el-form-item>
      <el-form-item v-if="!oneColumn" prop="" label="xx" style="opacity: 0;">
        &nbsp;
      </el-form-item>
      <el-form-item prop="name" label="企业名称">
        <el-input
          v-model="formValues.name"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="creditCode" label="统一社会信用代码">
        <el-input
          v-model="formValues.creditCode"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item prop="businessLicenseExpiryDate" label="营业执照有效期">
        <el-date-picker
          v-model="formValues.businessLicenseExpiryDate"
          type="daterange"
          clearable
          start-placeholder="创建时间(起)"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          end-placeholder="创建时间(止)"
          :disabled="disabled"
          @change="isLongTerm = false"
        />
        <el-checkbox
          v-model="isLongTerm"
          class="ml-[24px]"
          label="长期"
          :disabled="isLongTerm || disabled"
          @change="changeLongTerm()"
        />
      </el-form-item>
      <el-form-item prop="address" :label="addressLabel">
        <el-input
          v-model="formValues.address"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item v-if="props.showLegalName" prop="legalName" label="法定代表人姓名">
        <el-input
          v-model="formValues.legalName"
          clearable
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
      <el-form-item v-if="props.showBusinessScope" prop="businessScope" label="经营范围">
        <el-input
          v-model="formValues.businessScope"
          :rows="4"
          type="textarea"
          clearable
          resize="none"
          autocomplete="nony"
          placeholder="请输入"
          :disabled="disabled"
          @keyup.enter="onKeyupEnter"
        />
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { CardImageUpload } from '@vue3/components';
import { FormInstance, FormRules } from 'element-plus';
import { computed, PropType, ref, toRaw, watch } from 'vue';
import { ILicenseInfo } from './type';
import { changeFormatToDate, getFileExt, scrollToErrorField, changeTimeToFormat, longTimeValues } from '@core/tools';
import { UploadImageUrls } from '../../../../api/common';
import { getCustomState } from '../../../..';

const props = defineProps({
  data: {
    type: Object as PropType<ILicenseInfo>,
    default: () => {
      return {} as ILicenseInfo;
    },
  },
  disableAll: {
    type: Boolean,
    default: false,
  },
  disabledSomeFiled: {
    type: Boolean,
    default: false,
  },
  showLegalName: {
    type: Boolean,
    default: false,
  },
  showBusinessScope: {
    type: Boolean,
    default: false,
  },
  oneColumn: {
    type: Boolean,
    default: false,
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  /* 是否启用回车键 */
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  addressLabel: {
    type: String,
    default: '注册地址',
  },
  /**
   * 长期时间格式
   * @default 2999-12-31
   */
  longTimeFormat: {
    type: String,
    default: longTimeValues[2],
  },
});

const emit = defineEmits<{'key-enter': []}>();

const formRef = ref<FormInstance>();
const formValues = ref({} as ILicenseInfo);

const isLongTerm = ref(false);

const companyState = computed(() => getCustomState<ILicenseInfo>());

const disabled = computed(() => {
  return props.disableAll || props.disabledSomeFiled;
});

const rules = computed<FormRules<ILicenseInfo>>(() => {
  return !props.disableAll
    ? {
      businessLicensePic: [
        {
          required: true,
          message: '请上传营业执照照片',
          trigger: 'blur',
        },
      ],
      name: [
        {
          required: true,
          message: '请输入企业名称',
          trigger: 'blur',
        },
      ],
      creditCode: [
        {
          required: true,
          message: '请输入统一社会信用代码',
          trigger: 'blur',
        },
      ],
      address: [
        {
          required: true,
          message: '请输入注册地址',
          trigger: 'blur',
        },
      ],
      legalName: [
        {
          required: true,
          message: '请输入法定代表人姓名',
          trigger: 'blur',
        },
      ],
      businessScope: [
        {
          required: true,
          message: '请输入经营范围',
          trigger: 'blur',
        },
      ],
      businessLicenseExpiryDate: [
        {
          required: true,
          message: '请选择营业执照有效期',
          trigger: 'change',
        },
      ],
    }
    : {} as FormRules<ILicenseInfo>;
});

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    } else {
      if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    }
  });

  return valid;
}

function changeLongTerm() {
  if (isLongTerm.value) {
    const time = formValues.value.businessLicenseExpiryDate;

    formValues.value.businessLicenseExpiryDate = [!time || !time?.[0] ? '1950-01-01' : time[0], props.longTimeFormat];
    formRef.value?.validateField('businessLicenseExpiryDate');
  }
}

async function onKeyupEnter() {
  if (!props.enableEnterKey) return;

  const valid = await validate();

  if (!valid) {
    if (props.autoScrollToError) scrollToErrorField(formRef.value.$el);
    return;
  }

  emit('key-enter');
}

function resetFormValues() {
  const d = JSON.parse(JSON.stringify(props.data)) as ILicenseInfo;

  formRef.value?.resetFields?.();
  formValues.value = {
    businessLicensePic: d.businessLicensePic || companyState.value?.businessLicensePic,
    creditCode: d.creditCode || companyState.value?.creditCode,
    name: d.name || companyState.value?.name,
    address: d.address || companyState.value?.address,
    legalName: d.legalName,
    businessScope: props.showBusinessScope ? d.businessScope : undefined,
    businessLicenseStartDate: d.businessLicenseStartDate || companyState.value?.businessLicenseStartDate,
    businessLicenseEndDate: d.businessLicenseEndDate || companyState.value?.businessLicenseEndDate,
  };

  mergeDate(formValues.value);
}

function mergeDate(d: ILicenseInfo) {
  if (d.businessLicenseStartDate && d.businessLicenseEndDate) {
    d.businessLicenseExpiryDate = [
      changeFormatToDate(d.businessLicenseStartDate),
      changeFormatToDate(d.businessLicenseEndDate),
    ];
  }
  if (longTimeValues.includes(d.businessLicenseEndDate)) {
    isLongTerm.value = true;
  }
}

watch(
  () => props.data,
  () => {
    resetFormValues();
  },
  { immediate: true },
);

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => ({
    ...toRaw(formValues.value),
    businessLicenseExpiryDate: undefined,
    businessScope: props.showBusinessScope ? formValues.value.businessScope || undefined : undefined,
    businessLicensePicType: getFileExt(formValues.value.businessLicensePic),
    businessLicenseStartDate: formValues.value.businessLicenseExpiryDate?.[0]?.replace(/-/g, ''),
    businessLicenseEndDate: formValues.value.businessLicenseExpiryDate?.[1]?.replace(/-/g, ''),
  }),
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});
</script>

<style scoped lang="scss" src="../index.scss" />
